export interface TypographyDefinition {
  id: string;
  name: string;
  preview: string[];
  cssVars: Record<`--${string}`, string>;
}

export type TypographyId = TypographyDefinition['id'];

const typographyInternal: TypographyDefinition[] = [
  {
    id: 'neon-circuit',
    name: 'Neon circuit',
    preview: ['#6EE7F9', '#FFD166', '#F8FAFC'],
    cssVars: {
      '--font-family-base': "'Nova Flat', sans-serif",
      '--font-family-heading': "'Nova Flat', sans-serif",
      '--font-family-title': "'Nova Flat', sans-serif",
      '--font-size-body': '1.36rem',
      '--font-line-body': '1.22',
      '--font-size-base': '1.36rem',
      '--font-line-base': '1.22',
      '--font-size-10': '1rem',
      '--font-size-11': '1.08rem',
      '--font-size-xs': '1.18rem',
      '--font-size-sm': '1.3rem',
      '--font-size-list-title': '1.62rem',
      '--font-size-subtitle': '1.6rem',
      '--font-size-secondary-button': '1.56rem',
      '--font-size-legal': '1.24rem',
      '--font-size-label': '1.2rem',
      '--font-size-meta': '1.14rem',
      '--font-size-note': '1.16rem',
      '--font-size-button': '1.34rem',
      '--font-size-button-compact': '1.12rem',
      '--font-size-action-button': '1.76rem',
      '--font-size-cta': '2.08rem',
      '--font-size-pill': '1.32rem',
      '--font-size-panel-title': '1.84rem',
      '--font-size-panel-title-strong': '2rem',
      '--font-size-metric': '1.9rem',
      '--font-size-metric-strong': '2.08rem',
      '--font-size-display': '3.3rem',
      '--font-size-display-sidebar': '3.6rem',
      '--font-size-home-title-mobile': 'clamp(2.15rem, 9vw, 3.25rem)',
      '--font-line-home-title-mobile': '1.08',
      '--font-size-home-title-desktop': 'clamp(2.85rem, 3.4vw, 4rem)',
      '--font-line-home-title-desktop': '1.02',
    },
  },
  {
    id: 'chakra-petch',
    name: 'Turbo grid',
    preview: ['#8EF7D0', '#FFD166', '#F8FAFC'],
    cssVars: {
      '--font-family-base': "'Chakra Petch', sans-serif",
      '--font-family-heading': "'Chakra Petch', sans-serif",
      '--font-family-title': "'Chakra Petch', sans-serif",
      '--font-size-body': '1.36rem',
      '--font-line-body': '1.22',
      '--font-size-base': '1.36rem',
      '--font-line-base': '1.22',
      '--font-size-10': '1rem',
      '--font-size-11': '1.08rem',
      '--font-size-xs': '1.18rem',
      '--font-size-sm': '1.3rem',
      '--font-size-list-title': '1.62rem',
      '--font-size-subtitle': '1.6rem',
      '--font-size-secondary-button': '1.56rem',
      '--font-size-legal': '1.24rem',
      '--font-size-label': '1.2rem',
      '--font-size-meta': '1.14rem',
      '--font-size-note': '1.16rem',
      '--font-size-button': '1.34rem',
      '--font-size-button-compact': '1.12rem',
      '--font-size-action-button': '1.76rem',
      '--font-size-cta': '2.08rem',
      '--font-size-pill': '1.32rem',
      '--font-size-panel-title': '1.84rem',
      '--font-size-panel-title-strong': '2rem',
      '--font-size-metric': '1.9rem',
      '--font-size-metric-strong': '2.08rem',
      '--font-size-display': '3.3rem',
      '--font-size-display-sidebar': '3.6rem',
      '--font-size-home-title-mobile': 'clamp(2.15rem, 9vw, 3.25rem)',
      '--font-line-home-title-mobile': '1.08',
      '--font-size-home-title-desktop': 'clamp(2.85rem, 3.4vw, 4rem)',
      '--font-line-home-title-desktop': '1.02',
    },
  },
  {
    id: 'solar-grid',
    name: 'Solar grid',
    preview: ['#6EE7F9', '#FFD166', '#F8FAFC'],
    cssVars: {
      '--font-family-base': "'Bitcount Single', sans-serif",
      '--font-family-heading': "'Bitcount Single', sans-serif",
      '--font-family-title': "'Bitcount Single', sans-serif",
      '--font-size-body': '1.22rem',
      '--font-line-body': '1.22',
      '--font-size-base': '1.22rem',
      '--font-line-base': '1.22',
      '--font-size-10': '0.9rem',
      '--font-size-11': '0.98rem',
      '--font-size-xs': '1.06rem',
      '--font-size-sm': '1.18rem',
      '--font-size-list-title': '1.46rem',
      '--font-size-subtitle': '1.44rem',
      '--font-size-secondary-button': '1.4rem',
      '--font-size-legal': '1.12rem',
      '--font-size-label': '1.08rem',
      '--font-size-meta': '1.02rem',
      '--font-size-note': '1.04rem',
      '--font-size-button': '1.2rem',
      '--font-size-button-compact': '1rem',
      '--font-size-action-button': '1.58rem',
      '--font-size-cta': '1.86rem',
      '--font-size-pill': '1.18rem',
      '--font-size-panel-title': '1.66rem',
      '--font-size-panel-title-strong': '1.8rem',
      '--font-size-metric': '1.72rem',
      '--font-size-metric-strong': '1.88rem',
      '--font-size-display': '2.9rem',
      '--font-size-display-sidebar': '3.2rem',
      '--font-size-home-title-mobile': 'clamp(1.9rem, 8vw, 2.9rem)',
      '--font-line-home-title-mobile': '1.1',
      '--font-size-home-title-desktop': 'clamp(2.5rem, 3vw, 3.6rem)',
      '--font-line-home-title-desktop': '1.04',
    },
  },
  {
    id: 'iron-press',
    name: 'Iron press',
    preview: ['#F5E6C8', '#D4A853', '#3D2B1A'],
    cssVars: {
      '--font-family-base': "'Sankofa Display', sans-serif",
      '--font-family-heading': "'Sankofa Display', sans-serif",
      '--font-family-title': "'Sankofa Display', sans-serif",
      '--font-size-body': '1.42rem',
      '--font-line-body': '1.3',
      '--font-size-base': '1.42rem',
      '--font-line-base': '1.3',
      '--font-size-10': '1.06rem',
      '--font-size-11': '1.14rem',
      '--font-size-xs': '1.24rem',
      '--font-size-sm': '1.36rem',
      '--font-size-list-title': '1.7rem',
      '--font-size-subtitle': '1.68rem',
      '--font-size-secondary-button': '1.64rem',
      '--font-size-legal': '1.3rem',
      '--font-size-label': '1.26rem',
      '--font-size-meta': '1.2rem',
      '--font-size-note': '1.22rem',
      '--font-size-button': '1.4rem',
      '--font-size-button-compact': '1.18rem',
      '--font-size-action-button': '1.84rem',
      '--font-size-cta': '2.14rem',
      '--font-size-pill': '1.38rem',
      '--font-size-panel-title': '1.92rem',
      '--font-size-panel-title-strong': '2.1rem',
      '--font-size-metric': '1.98rem',
      '--font-size-metric-strong': '2.16rem',
      '--font-size-display': '3.4rem',
      '--font-size-display-sidebar': '3.7rem',
      '--font-size-home-title-mobile': 'clamp(2.2rem, 9.5vw, 3.4rem)',
      '--font-line-home-title-mobile': '1.1',
      '--font-size-home-title-desktop': 'clamp(2.9rem, 3.5vw, 4.2rem)',
      '--font-line-home-title-desktop': '1.04',
    },
  },
] as const;

export const DEFAULT_TYPOGRAPHY_ID: TypographyId = 'neon-circuit';

const typographyMap = new Map<TypographyId, TypographyDefinition>(
  typographyInternal.map((typography) => [typography.id, typography]),
);


const ALL_TYPOGRAPHY_VARS = Array.from(
  typographyInternal.reduce((acc, typography) => {
    Object.keys(typography.cssVars).forEach((key) => acc.add(key));
    return acc;
  }, new Set<string>()),
);

const TYPOGRAPHY_STORAGE_KEY = 'dice1k_typography';

export function isValidTypographyId(id: unknown): id is TypographyId {
  return typeof id === 'string' && typographyMap.has(id as TypographyId);
}

export function getTypography(id: string | null | undefined): TypographyDefinition {
  if (id && typographyMap.has(id as TypographyId)) {
    return typographyMap.get(id as TypographyId)!;
  }
  return typographyMap.get(DEFAULT_TYPOGRAPHY_ID)!;
}

export function getTypographyList(): TypographyDefinition[] {
  return typographyInternal.slice();
}

export function applyTypography(typographyId: string): void {
  if (typeof document === 'undefined') return;
  const typography = getTypography(typographyId);
  const root = document.documentElement;
  root.dataset.typography = typography.id;
  ALL_TYPOGRAPHY_VARS.forEach((name) => root.style.removeProperty(name));
  Object.entries(typography.cssVars).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

export function readStoredTypographyId(): TypographyId | null {
  if (typeof window === 'undefined') return null;
  const stored = window.localStorage.getItem(TYPOGRAPHY_STORAGE_KEY);
  return isValidTypographyId(stored) ? stored : null;
}

export function storeTypographyId(typographyId: string): void {
  if (typeof window === 'undefined') return;
  const next = getTypography(typographyId).id;
  window.localStorage.setItem(TYPOGRAPHY_STORAGE_KEY, next);
}
